<template>
  <div>
    <!-- Table Container Card -->
    <b-card
      no-body
    >
      <payroll-list-add-new
        @created="onPayrollsCreated"
      />
      <div class="m-2">

        <!-- Table Top -->
        <b-row class="mb-1">
          <b-col cols="12">
            <span>Yhteensä: <span style="font-weight: 700">{{ totalPending ? parseFloat(totalPending).toFixed(2) : 0 }}€</span></span>
          </b-col>
        </b-row>
        <b-row>
          <!-- Per Page -->
          <b-col
            cols="12"
            md="10"
            class="d-flex align-items-center mb-1 mb-md-0"
          >
            <b-button
              v-b-modal.create-payroll-modal
              variant="outline-info"
              pill
            >
              {{ $t('payroll.btn.createPayroll') }}
            </b-button>
            <b-button
              class="ml-1"
              pill
              variant="outline-secondary"
              :disabled="payrollsSelected.length === 0"
              @click="exportCSV"
            >
              {{ $t('payroll.btn.exportCSV') }}
            </b-button>
            <b-button
              class="ml-1"
              variant="outline-info"
              pill
              :disabled="payrollsSelected.length === 0"
              @click="handleRowSelected('send')"
            >
              {{ $t('payroll.btn.send') }}
            </b-button>
            <b-button
              class="ml-1"
              variant="outline-info"
              pill
              :disabled="payrollsSelected.length === 0"
              @click="handleRowSelected('download')"
            >
              {{ $t('payroll.btn.downloadPDF') }}
            </b-button>
            <b-button
              class="ml-1"
              pill
              :variant="statusFilter.value === 'pending' ? 'outline-primary' : 'outline-warning'"
              :disabled="payrollsSelected.length === 0"
              @click="handleRowSelected('mark_paid', statusFilter.value === 'pending' ? true: false)"
            >
              {{ statusFilter.value === 'pending' ? $t('payroll.btn.markAsPaid') : $t('payroll.btn.notPaid') }}
            </b-button>
            <b-button
              v-b-modal.modal-delete-payrolls
              class="ml-1"
              pill
              variant="outline-danger"
              :disabled="payrollsSelected.length === 0"
            >
              {{ $t('payroll.btn.delete') }}
            </b-button>
          </b-col>

          <!-- Search -->
          <b-col
            cols="12"
            md="2"
          >
            <div class="d-flex align-items-center justify-content-end">
              <!--<b-form-input
                v-model="searchQuery"
                class="d-inline-block mr-1"
                placeholder="Search..."
              />-->
              <v-select
                v-model="statusFilter"
                :dir="$store.state.hwUiLayout.isRTL ? 'rtl' : 'ltr'"
                :options="statusOptions"
                :clearable="false"
                class="invoice-filter-select"
                placeholder="Select Status"
              >
                <template #selected-option="{ label }">
                  <span class="text-truncate overflow-hidden">
                    {{ label }}
                  </span>
                </template>
              </v-select>
            </div>
          </b-col>
        </b-row>
      </div>

      <b-table
        ref="refInvoiceListTable"
        :items="fetchPayrolls"
        responsive
        :fields="tableColumns"
        primary-key="id"
        :sort-by.sync="sortBy"
        show-empty
        :empty-text="$t('no_matching_records')"
        :sort-desc.sync="isSortDirDesc"
        class="position-relative"
        selectable
        @row-selected="onRowSelected"
      >

        <template #head(selected)>
          <b-form-checkbox
            v-model="checkAll"
            @change="value => value ? $refs.refInvoiceListTable.selectAllRows() : $refs.refInvoiceListTable.clearSelected()"
          />
        </template>
        <template #cell(selected)="row">
          <b-form-checkbox
            :checked="row.rowSelected"
            @change="value => value ? $refs.refInvoiceListTable.selectRow(row.index) : $refs.refInvoiceListTable.unselectRow(row.index)"
          />
        </template>

        <!-- Column: Id -->
        <template #cell(laskuNumero)="data">
          <b-link
            :to="{ name: 'apps-invoices-view', params: { id: data.item.id }}"
            class="font-weight-bold"
          >
            #{{ data.value }}
          </b-link>
        </template>

        <!-- Column: Invoice Status -->
        <template #cell(status)="data">
          <div style="margin-block: 10px;">
            <b-avatar
              v-if="!data.item.edited"
              :id="`invoice-row-${data.item.id}`"
              size="32"
              :variant="`light-${resolveInvoiceStatusVariantAndIcon(data.item.status).variant}`"
            >
              <feather-icon
                :icon="resolveInvoiceStatusVariantAndIcon(data.item.status).icon"
              />
            </b-avatar>
            <b-tooltip
              :title="resolveInvoiceStatusVariantAndIcon(data.item.status).title"
              :target="`invoice-row-${data.item.id}`"
            />
            <div>
              <b-badge
                v-if="data.item.edited"
                pill
                variant="light-secondary"
              >
                Muokattu 
              </b-badge>
            </div>
          </div>
        </template>
        <!-- Column: Client -->
        <template #cell(user)="data">
          <b-media>
            <div class="d-flex justify-content-between">
              <div>
                <b-link
                  class="font-weight-bold"
                  style="text-transform: capitalize; margin-left: -5px;"
                  :to="{ name: 'apps-personels-view', params: { id: data.item.userId }}"
                >
                  <b-avatar
                    size="32"
                    style="margin-right: 10px;"
                    :src="data.item.avatar"
                    :text="avatarText(data.item.user.fullName)"
                    :variant="`light-${resolveClientAvatarVariant(data.item.status)}`"
                  />
                </b-link>
                <b-link
                  class="font-weight-bold"
                  style="text-transform: capitalize"
                  :to="{ name: 'apps-payrolls-view', params: { id: data.item.id }}"
                >
                  {{ data.item.type === 'quick' ? data.item.user.fullName + ' - Pikapalkka' : data.item.user.fullName }}
                </b-link>
              </div>
              <div>
                <b-badge
                  v-if="data.item.user.isUserNew"
                  pill
                  variant="light-info"
                >
                  Uusi
                </b-badge>
                <b-badge
                  v-if="data.item.user.isJoiku"
                  pill
                  variant="light-secondary"
                >
                  Joiku 
                </b-badge>
                <b-badge
                  v-if="
                    data.item.user.tempContract &&
                      data.item.user.tempContract.some(item => tempContractIsActive(item.startDate, item.endDate))
                  "
                  pill
                  variant="light-success"
                  class="text-capitalize"
                >
                  <feather-icon icon="CheckCircleIcon" size="18" class="mr-50" />
                  {{ $t('personel.badge.temporaryWorker') }}
                </b-badge>
                <b-badge
                  v-if="data.item.hasJaksoOverHours"
                  pill
                  variant="light-warning"
                >
                  <feather-icon icon="AlertCircleIcon" size="18" class="mr-50" />
                  Jakso ylityöt 
                </b-badge>
                <b-badge
                  v-if="data.item.bonus"
                  pill
                  variant="light-warning"
                >
                  <feather-icon icon="AlertCircleIcon" size="18" class="mr-50" />
                  Bonus {{ data.item.bonus }}€
                </b-badge>
                <b-badge
                  v-if="data.item.user.specials && data.item.user.specials.distraint"
                  style="margin-inline: 2px;"
                  pill
                  variant="light-danger"
                >
                  {{ $t('distraint') }}
                </b-badge>
              </div>
            </div>
          </b-media>
        </template>
        <!-- Column: title -->
        <template #cell(name)="data">
          <b-link
            class="font-weight-bold"
            style="text-transform: capitalize"
            :to="{ name: 'apps-invoice-payroll', params: { id: data.item.id }}"
          >
            {{ data.value }}
          </b-link>
        </template>

        <!-- Column: Issued Date -->
        <template #cell(date)="data">
          <span class="text-nowrap">
            {{ data.value.toDate().toLocaleDateString('fi') }}
          </span>
        </template>

        <!-- Column: Balance -->
        <template #cell(total)="data">
          <template v-if="data.value === 0">
            <b-badge
              pill
              variant="light-success"
            >
              Paid
            </b-badge>
          </template>
          <template v-else>
            {{ data.value }}
          </template>
        </template>

        <!-- Column: Actions -->
        <template #cell(actions)="data">

          <div class="text-nowrap">
            <feather-icon
              :id="`invoice-row-${data.item.id}-preview-icon`"
              icon="EyeIcon"
              size="16"
              class="mx-1"
              @click="$router.push({ name: 'apps-payrolls-view', params: { id: data.item.id }})"
            />
            <b-tooltip
              title="Esikatsele"
              :target="`invoice-row-${data.item.id}-preview-icon`"
            />

            <!-- Dropdown -->
            <b-dropdown
              variant="link"
              toggle-class="p-0"
              no-caret
              :right="$store.state.hwUiLayout.isRTL"
            >

              <template #button-content>
                <feather-icon
                  icon="MoreVerticalIcon"
                  size="16"
                  class="align-middle text-body"
                />
              </template>
              <b-dropdown-item :to="{ name: 'apps-payrolls-edit', params: { id: data.item.id } }">
                <feather-icon icon="EditIcon" />
                <span class="align-middle ml-50">{{ $t('edit') }}</span>
              </b-dropdown-item>
              <b-dropdown-item
                v-b-modal.modal-danger
                @click="onDeleteInvoice(data.item)"
              >
                <feather-icon icon="TrashIcon" />
                <span class="align-middle ml-50">{{ $t('delete') }}</span>
              </b-dropdown-item>
            </b-dropdown>
          </div>
        </template>

      </b-table>
    </b-card>
    <b-modal
      id="modal-danger"
      ok-only
      ok-variant="danger"
      :ok-title="$t('delete')"
      modal-class="modal-danger"
      centered
      title="Poista palkkalaskelma"
      @ok="confirmDelete"
    >
      <b-card-text>
        Olet poistamassa palkkalaskelman, tätä toimintoa ei voi perua!
      </b-card-text>
    </b-modal>
    <b-modal
      id="modal-delete-payrolls"
      ok-only
      ok-variant="danger"
      :ok-title="$t('delete')"
      modal-class="modal-danger"
      centered
      title="Poista valitut palkkalaskelmat"
      @ok="handleRowSelected('delete')"
    >
      <b-card-text>
        Olet poistamassa useita palkkalaskelmia, tätä toimintoa ei voi perua!
      </b-card-text>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BFormCheckbox, BButton, BTable, BAvatar, BLink,
  BBadge, BDropdown, BDropdownItem, BTooltip,
  BModal, VBModal, BCardText, BMedia,
} from 'bootstrap-vue'
import i18n from '@hw-core/libs/i18n'
// eslint-disable-next-line import/no-cycle
import { avatarText } from '@hw-ui/utils/filter'
import vSelect from 'vue-select'
import Ripple from 'vue-ripple-directive'
import PayrollListAddNew from './PayrollListAddNew.vue'
import usePayrollList from './usePayrollList'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BFormCheckbox,
    BButton,
    BTable,
    BAvatar,
    BLink,
    BBadge,
    BMedia,
    BDropdown,
    BDropdownItem,
    BTooltip,
    BModal,
    BCardText,
    vSelect,
    PayrollListAddNew,
  },
  directives: {
    'b-modal': VBModal,
    Ripple,
  },
  computed: {
    appConfig() {
      return this.$store.state.app.appConfig || {}
    },
  },
  setup() {
    const statusOptions = [
    {
        label: i18n.t('pending'),
        value: 'pending',
      },
      {
        label: i18n.t('paid'),
        value: 'paid',
      },
    ]

    const {
      fetchPayrolls,
      tableColumns,
      perPage,
      currentPage,
      totalInvoices,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refInvoiceListTable,

      statusFilter,

      refetchData,

      resolveInvoiceStatusVariantAndIcon,
      resolveClientAvatarVariant,
      onDeleteInvoice,
      confirmDelete,
      onPayrollsCreated,
      totalPending,
      exportCSV,
      payrollsSelected,
      onRowSelected,
      handleRowSelected,
      tempContractIsActive,
      checkAll,
      changePage,
    } = usePayrollList()

    return {
      fetchPayrolls,
      tableColumns,
      perPage,
      currentPage,
      totalInvoices,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refInvoiceListTable,

      statusFilter,
      tempContractIsActive,
      refetchData,

      statusOptions,

      avatarText,
      resolveInvoiceStatusVariantAndIcon,
      resolveClientAvatarVariant,
      onDeleteInvoice,
      confirmDelete,
      onPayrollsCreated,
      totalPending,
      exportCSV,
      payrollsSelected,
      onRowSelected,
      handleRowSelected,
      checkAll,
      changePage,
    }
  },
}
</script>

<style lang="scss" scoped>
@import "~@hw-ui/scss/base/pages/app-invoice.scss";
.per-page-selector {
  width: 90px;
}

.invoice-filter-select {
  min-width: 190px;

  ::v-deep .vs__selected-options {
    flex-wrap: nowrap;
  }

  ::v-deep .vs__selected {
    width: 100px;
  }
}
</style>

<style lang="scss">
@import '@hw-ui/scss/vue/libs/vue-select.scss';

@media print {
  // Global Styles
  body {
    background-color: transparent !important;
  }
  nav.header-navbar {
    display: none;
  }
  .main-menu {
    display: none;
  }
  .header-navbar-shadow {
    display: none !important;
  }
  .content.app-content {
    margin-left: 0;
    padding-top: 2rem !important;
  }
  footer.footer {
    display: none;
  }
  .card {
    background-color: transparent;
    box-shadow: none;
  }
  .customizer-toggle {
    display: none !important;
  }

  // Invoice Specific Styles
  .invoice-preview-wrapper {
    .row.invoice-preview {
      .col-md-8 {
        max-width: 100%;
        flex-grow: 1;
      }

      .invoice-preview-card {
        .card-body:nth-of-type(2) {
          .row {
              > .col-12 {
              max-width: 50% !important;
            }

            .col-12:nth-child(2) {
              display: flex;
              align-items: flex-start;
              justify-content: flex-end;
              margin-top: 0 !important;
            }
          }
        }
      }
    }

    // Action Right Col
    .invoice-actions {
      display: none;
    }
  }
}
</style>
